import { useEffect, useRef } from 'react';

/**
 * Custom hook that returns the previous state value.
 *
 * @template T - The type of the state value.
 * @param {T} value - The current state value.
 * @returns {T | undefined} - The previous state value.
 */
function usePreviousState<T>(value: T): T | undefined {
  const previousRef = useRef<T>();

  useEffect(() => {
    /**
     * Assigns the latest render value to the ref.
     * However, assigning a value to ref doesn't trigger a re-render.
     * So, `previousRef.current` in the return statement displays the
     * last value in the ref at the time of render, i.e., the previous state value.
     */
    previousRef.current = value;

    return (): void => (previousRef.current = undefined);
  }, [value]);

  return previousRef.current;
}

export default usePreviousState;
